import { useCallback } from "react";

import type { GuestPageVisibilityMode, SnackList } from "@/lib/gql/__generated__/graphql";
import { useCopyToClipboard } from "@/lib/react-use/use-copy-to-clipboard";

import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";

import type { FormOptionType } from "@/components/ui/form/_types/type";

import { resolveVisibility } from "@/components/features-new/guest-page-visibility";

import {
	DEFAULT_TOP_PAGE_VALUE,
	snackListToTopPageSnackOptions,
} from "../converter/snack-list-to-top-page-snack-options";

import type { DesignSnackSettingsFormModelOnSetViewModelType } from "..";
import type { DesignSnackSettingsFormModel } from "../type";

type Props = {
	viewModel: DesignSnackSettingsFormModel["ViewModel"];
	onSetViewModel: DesignSnackSettingsFormModelOnSetViewModelType;
	snackList: SnackList;
	/** 保存済みのスナックの公開状態（チェックを外したときに戻す値） */
	savedGuestPageVisibility: GuestPageVisibilityMode;
};

export type SnackFormBaseModelMethodsType = {
	canCopySnackPageURL: boolean;
	copySnackPageURL: () => void;
	onSetDomainName: (input: string) => void;

	onSetGtmID: (input: string) => void;

	topPageSnackValue: string;
	topPageSnackOptions: FormOptionType[];
	onSetTopPageSnackValue: (value: string) => void;

	onSetGuestPageUnlisted: (isUnlisted: boolean) => void;
};

export const useSnackSettingsFormModelMethods = ({
	viewModel,
	onSetViewModel,
	snackList,
	savedGuestPageVisibility,
}: Props): SnackFormBaseModelMethodsType => {
	const { hasError } = viewModel;

	const { copy } = useCopyToClipboard();
	const canCopySnackPageURL = !hasError && !!viewModel.subDomain.value;
	const copySnackPageURL = useCallback(() => {
		if (hasError) return;
		const URL = `https://${viewModel.subDomain.value}.${viewModel.baseDomain}`;

		copy(URL);
		notifyUserEventInfo({
			title: "URLをコピーしました",
		});
	}, [copy, hasError, viewModel.baseDomain, viewModel.subDomain.value]);

	const onSetDomainName = useCallback(
		(input: string) => {
			onSetViewModel([
				{
					key: "subDomain",
					value: {
						isEdited: true,
						value: input,
					},
				},
			]);
		},
		[onSetViewModel],
	);

	const onSetGtmID = useCallback(
		(input: string) => {
			onSetViewModel([
				{
					key: "gtmID",
					value: {
						isEdited: true,
						value: input,
					},
				},
			]);
		},
		[onSetViewModel],
	);

	const topPageSnackUUID = viewModel.topPageSnackUUID?.value;
	const topPageSnackOptions = snackListToTopPageSnackOptions({ snackList, topPageSnackUUID });

	const onSetTopPageSnackValue = useCallback(
		(value: string) => {
			onSetViewModel([
				{
					key: "topPageSnackUUID",
					value: {
						isEdited: true,
						value: value === DEFAULT_TOP_PAGE_VALUE ? null : value,
					},
				},
			]);
		},
		[onSetViewModel],
	);

	// チェックありは検索に表示しない。外したときは保存済みの値に戻す（未設定を公開で上書きしない）
	const onSetGuestPageUnlisted = useCallback(
		(isUnlisted: boolean) => {
			onSetViewModel([
				{
					key: "guestPageVisibility",
					value: {
						isEdited: true,
						value: resolveVisibility(isUnlisted, savedGuestPageVisibility),
					},
				},
			]);
		},
		[onSetViewModel, savedGuestPageVisibility],
	);

	return {
		canCopySnackPageURL,
		copySnackPageURL,
		onSetDomainName,
		onSetGtmID,

		topPageSnackValue: topPageSnackUUID ?? DEFAULT_TOP_PAGE_VALUE,
		topPageSnackOptions,
		onSetTopPageSnackValue,

		onSetGuestPageUnlisted,
	};
};
